<template>
    <div v-if="courses.length" class="tm-card yc">
        <h2 class="yc__title">Your courses</h2>
        <ul class="yc__list">
            <li v-for="c in courses" :key="c.course_id" class="yc__item">
                <a class="yc__name" :href="c.url" target="_blank" rel="noopener">{{ c.title }}</a>
                <div class="yc__row">
                    <span class="yc__status" :class="'yc__status--' + c.status">{{ statusText(c) }}</span>
                    <span v-if="c.status !== 'completed'" class="yc__ask">
                        <button type="button" class="yc__btn yc__btn--ghost" :aria-label="`Remove ${c.title}`" @click="remove(c)">Remove</button>
                        <button v-if="c.status === 'started' && c.due" type="button" class="yc__btn" @click="later(c)">Not yet</button>
                        <button v-if="c.status === 'started'" type="button" class="yc__btn yc__btn--primary" @click="checkin = c">Finished</button>
                    </span>
                </div>
            </li>
        </ul>
    </div>

    <teleport to="body">
        <div v-if="checkin" class="tm-scope yc-backdrop" @click.self="checkin = null">
            <div class="yc-dialog" role="dialog" aria-modal="true" aria-labelledby="yc-dialog-title" @keydown.esc="checkin = null">
                <h2 id="yc-dialog-title" class="yc-dialog__title">Did you finish “{{ checkin.title }}”?</h2>
                <label for="yc-cert" class="yc-dialog__label">Certificate link</label>
                <input id="yc-cert" ref="certInput" v-model="certUrl" type="url" class="tm-field" placeholder="https://coursera.org/verify/…">
                <p v-if="error" class="yc-dialog__error">{{ error }}</p>
                <div class="yc-dialog__actions">
                    <button type="button" class="tm-btn-primary yc-dialog__main" :disabled="busy || !certUrl" @click="complete(true)">{{ busy ? 'Checking…' : 'Add verified skills' }}</button>
                    <button type="button" class="tm-btn-secondary" :disabled="busy" @click="complete(false)">Finished, no certificate</button>
                </div>
                <button type="button" class="yc-dialog__later" :disabled="busy" @click="later(checkin)">Not yet, remind me in 2 weeks</button>
            </div>
        </div>
    </teleport>
</template>

<script>
import { nextTick, onMounted, ref, watch } from 'vue'
import { CourseApiService } from '@/services/CourseApiService'

// The person's courses (saved / started / completed) and the follow-up: "Did you finish?"
// -> the course's skills join the CV (verified with a certificate link) -> parent reloads.
export default {
    name: 'YourCourses',
    props: { occupationId: { type: [Number, String], default: null }, refreshKey: { type: Number, default: 0 } },
    emits: ['completed'],
    setup(props, { emit }) {
        const courses = ref([])
        const checkin = ref(null)
        const certUrl = ref('')
        const busy = ref(false)
        const error = ref('')
        const certInput = ref(null)

        const load = () => CourseApiService.Plan().then(res => { courses.value = res.data.courses }).catch(() => {})
        onMounted(load)
        watch(() => props.refreshKey, load)
        watch(checkin, c => {
            certUrl.value = ''
            error.value = ''
            if (c) nextTick(() => certInput.value && certInput.value.focus())
        })

        async function complete(withCert) {
            busy.value = true
            error.value = ''
            try {
                const c = checkin.value
                const res = await CourseApiService.UpdatePlan(c.course_id, 'completed', {
                    occupation_id: c.occupation_id || Number(props.occupationId) || null,
                    certificate_url: withCert ? certUrl.value.trim() : null,
                })
                checkin.value = null
                await load()
                emit('completed', { title: c.title, added: res.data.added, verified: res.data.verified })
            } catch (e) {
                // The certificate didn't check out (not found, other course, other name, already used):
                // say why; the person can fix the link or choose "finished, no certificate".
                error.value = (e.response && e.response.data && e.response.data.error) || 'Something went wrong. Please try again.'
            } finally {
                busy.value = false
            }
        }

        async function later(c) {
            await CourseApiService.UpdatePlan(c.course_id, 'later')
            checkin.value = null
            await load()
        }

        // Undo a mistaken save / click-out: the course leaves the plan (status dropped).
        async function remove(c) {
            courses.value = courses.value.filter(x => x.course_id !== c.course_id)
            await CourseApiService.UpdatePlan(c.course_id, 'dropped').catch(load)
        }

        return {
            courses, checkin, certUrl, busy, error, certInput, complete, later, remove,
            statusText: c => ({
                saved: 'Saved',
                started: c.days ? `Started ${c.days} ${c.days === 1 ? 'day' : 'days'} ago` : 'Started today',
                completed: c.verified === 'certificate' ? 'Verified · on your CV' : 'Completed · on your CV',
            }[c.status] || c.status),
        }
    },
}
</script>

<style scoped>
.yc { padding: 20px 22px; }
.yc__title { margin: 0 0 6px; font-size: 15px; font-weight: 700; }
.yc__list { list-style: none; margin: 0; padding: 0; }
.yc__item { padding: 10px 0; border-bottom: 1px solid var(--tm-border-soft); display: flex; flex-direction: column; gap: 6px; }
.yc__item:last-child { border-bottom: 0; }
.yc__name { font-size: 13.5px; font-weight: 600; line-height: 1.35; color: var(--tm-ink); text-decoration: none; }
.yc__name:hover { color: var(--tm-accent); }
.yc__row { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.yc__status { font-size: 12px; font-weight: 600; color: var(--tm-ink-muted); }
.yc__status--started { color: var(--tm-partial); }
.yc__status--completed { color: var(--tm-have); }
.yc__ask { display: flex; gap: 6px; }
.yc__btn {
    height: 30px; padding: 0 11px; border-radius: 7px; border: 1px solid var(--tm-border); background: var(--tm-surface);
    font: inherit; font-size: 12px; font-weight: 600; color: var(--tm-ink-muted); cursor: pointer;
}
.yc__btn--primary { border: 0; background: var(--tm-accent); color: #FFFFFF; }
.yc__btn--ghost { border: 0; background: none; padding: 0 6px; }
.yc__btn--ghost:hover { color: var(--tm-gap); }

.yc-backdrop { position: fixed; inset: 0; z-index: 1060; background: rgba(11, 34, 57, 0.38); display: flex; align-items: center; justify-content: center; padding: 16px; }
.yc-dialog {
    width: min(520px, 100%); box-sizing: border-box; background: var(--tm-surface); border-radius: 16px; padding: 28px 30px;
    display: flex; flex-direction: column; gap: 14px; box-shadow: 0 24px 60px rgba(11, 34, 57, 0.25);
}
.yc-dialog__title { margin: 0; font-family: var(--tm-font-display); font-size: 22px; line-height: 1.25; color: var(--tm-ink); }
.yc-dialog__label { font-size: 13px; font-weight: 600; color: var(--tm-ink-soft); margin: 4px 0 -6px; }
.yc-dialog__error { margin: 0; font-size: 13px; color: var(--tm-gap); }
.yc-dialog__actions { display: flex; gap: 10px; flex-wrap: wrap; }
.yc-dialog__main { flex: 1; }
.yc-dialog__later { border: 0; background: none; font: inherit; font-size: 13px; font-weight: 600; color: var(--tm-ink-muted); cursor: pointer; padding: 8px; }
</style>
